import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatSort, MatTable } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Company, Option } from '@core/interfaces';
import { mapIds } from '@core/utils/filters-utils';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { DialogEditCriteriasGroupComponent } from './components/dialogs/dialog-edit-criterias-group/dialog-edit-criterias-group.component';
import { FilterComponent } from './components/filter/filter.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { CriteriaGroupOption, CriteriasGroups } from './interfaces';
import { CompanyRankingMapperService } from './services/company-raking.mapper.service';
import { CompanyRankingService } from './services/company-ranking.service';
import { FilterStateManagerService } from './services/filter-sate-manager.service';
import { defaultCriteriaGroup } from './utils/default-criteria-group';

@Component({
  selector: 'app-company-ranking',
  templateUrl: './company-ranking.component.html',
  styleUrls: ['./company-ranking.component.scss']
})
export class CompanyRankingComponent implements OnInit, AfterViewChecked, OnDestroy {

  @ViewChild(FilterComponent, { static: true })
  public filter: FilterComponent;
  @ViewChild(MatSort, { static: true })
  public sort: MatSort;
  @ViewChild('datatable', { static: true })
  public datatable: MatTable<any>;
  @ViewChild('content', { static: false })
  public contentElementRef: ElementRef;

  public isLoading: Boolean = true;
  public hasCriteriaData: Boolean = false;
  public filterSubscription: Subscription;
  public selectedCompaniesHasChanged: Boolean = false;
  public criteriasGroups: CriteriasGroups[] = [];
  public companies: Company[] = [];
  public selectedCompanies: Company[] = [];
  public dataTableSource: any[] = [];
  public displayedColumns: string[] = [];
  public criteriaKeys: string[] = [];
  public selectedCompaniesSelection = new SelectionModel<string>(true, []);
  public routeName = this.route.snapshot.routeConfig.path;
  public isHorizontalScrollableTable = false;
  public selectedCompaniesLimit = 4;
  public filterHeight = 66;
  public intervalID: NodeJS.Timer;
  public isPercentage = [
    'acceptedPercentage',
    'refusedPercentage',
    'completedPercentage',
    'incompletedPercentage',
    'responseOnTimePercentage',
    'solutionOnTimePercentage',
    'responseOnTimePercentageByRange',
    'solutionOnTimePercentageByRange'
  ];
  public isTimeAverage = [
    'averageResponseTimeInDays',
    'averageCloseTimeInDays'
  ];
  public isTimeAverageInSeconds = [
    'requestedInterval',
    'onNegotiationInterval',
    'pendingInterval',
    'scheduledInterval',
    'inProgressInterval',
    'reportedInterval',
    'responseInterval',
    'solutionInterval',
  ];

  private autoReloadTime: number = 2 * 60000;
  public sortDirection: 'asc' | 'desc' = 'desc';

  constructor(
    private cdr: ChangeDetectorRef,
    private readonly router: Router,
    private readonly matDialog: MatDialog,
    private readonly route: ActivatedRoute,
    private readonly companyRankingService: CompanyRankingService,
    private readonly authenticationService: AuthenticationService,
    private readonly companyRankingMapper: CompanyRankingMapperService,
    public readonly filterStateManagerService: FilterStateManagerService,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public async ngOnInit(): Promise<void> {
    await this.filterStateManagerService.getSavedData(this.filter, this.routeName);
    this.criteriasGroups = await this.companyRankingService.getCriteriasGroups();
    if (!this.criteriasGroups || !this.criteriasGroups.length) {
      await this.createDefaultCriteriaGroup();
    }
    this.subscribeToFilterValueChanges();
  }

  public ngAfterViewChecked(): void {
    this.setTableColumnsWidth();
  }

  public ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }

    if (this.filter.filtersForm.invalid) {
      this.filterStateManagerService.isFilterOn = false;
      return;
    }

    this.filterStateManagerService.selectedOption = null;
    this.filterStateManagerService.setDataOnLocalStorage(this.filter, this.routeName);
  }

  public subscribeToFilterValueChanges(): void {
    this.filterSubscription = this.filter.filtersForm.valueChanges
      .pipe(
        filter(() => this.filter.filtersForm.valid),
        tap(() => this.isLoading = true),
        debounceTime(700),
        tap(() => {
          const currentWhere = this.filterStateManagerService.toQuery(this.filter.filtersForm.value);
          const defaultWhere = this.filterStateManagerService.toQuery(this.filter.defaultFilterValues);
          this.filterStateManagerService.isFilterOn = !isEqual(currentWhere, defaultWhere);
        }),
        switchMap(() => this.updateDataTableSource())
      ).subscribe(() => {
        this.isLoading = false;
        this.filterStateManagerService.setDataToQueryParams(this.filter, this.routeName);
        setTimeout(() => this.setContentHeight(), 0);
      });
  }

  public toggleCompany(company: Company): void {
    const alreadySelected = this.selectedCompaniesSelection.isSelected(company.id);
    const didntReachTheLimit = this.selectedCompaniesSelection.selected.length < this.selectedCompaniesLimit;

    if (!alreadySelected && didntReachTheLimit) {
      this.selectedCompaniesSelection.select(company.id);
      this.selectedCompanies.push(company);
    } else {
      this.selectedCompaniesSelection.deselect(company.id);
      this.selectedCompanies = this.selectedCompanies.filter((selectedCompany) => selectedCompany.id !== company.id);
    }
  }

  public async handleOptionSelected(selectedOption: CriteriaGroupOption): Promise<void> {
    this.isLoading = true;
    this.filterStateManagerService.selectedOption = selectedOption;
    await this.updateDataTableSource();
    this.isLoading = false;
  }

  public handleEditOption(option: Option): void {
    const dialogEditCriteriasGroupComponent = this.matDialog.open(DialogEditCriteriasGroupComponent, {
      panelClass: ['field-dialog-860'],
      data: {
        criteriaId: option.id,
        deletionShouldBeEnabled: this.criteriasGroups.length > 1
      }
    });

    dialogEditCriteriasGroupComponent.afterClosed().subscribe(async (result) => {
      if (result) {
        this.criteriasGroups = await this.companyRankingService.getCriteriasGroups();
        this.updateSelectedCriteriaGroup();
        this.reload();
      }
    });
  }

  public handleAddOption(): void {
    const dialogAddCriteriasGroupComponent = this.matDialog.open(DialogEditCriteriasGroupComponent, {
      panelClass: ['field-dialog-860']
    });

    dialogAddCriteriasGroupComponent.afterClosed().subscribe(async (result) => {
      if (result) {
        this.criteriasGroups = await this.companyRankingService.getCriteriasGroups();
        this.updateSelectedCriteriaGroup();
        this.reload();
      }
    });
  }

  public async updateDataTableSource(): Promise<void> {
    const where = this.filterStateManagerService.toQuery({ ...this.filter.filtersForm.value });
    const [criteriaGroup, criteriasData] = await Promise.all([
      this.companyRankingService.getCriteriasGroupById(this.filterStateManagerService.selectedOption.id),
      this.companyRankingService.criteriasDataByCriteriasGroupId(this.filterStateManagerService.selectedOption.id, where)
    ]);

    this.dataTableSource = [];
    this.dataTableSource.push(...this.companyRankingMapper.mapToDataTable(criteriaGroup, criteriasData).sort((a, b) => {
      return this.orderByTotalPointsAndName(a, b);
    }));
    if (!this.dataTableSource.length) {
      return;
    }
    this.criteriaKeys = Object.keys(this.dataTableSource[0].criterias);
    this.displayedColumns = ['checkbox', 'position', 'name', ...this.criteriaKeys];
  }

  public getRuleNameByCriteriaId (criteriaId: string): string {
    const criterias = this.dataTableSource[0].criterias;

    return criterias[criteriaId].ruleName || '';
  }

  public orderByPoints(): void {
    this.isLoading = true;
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';

    this.dataTableSource.sort((a, b) => {
      return this.orderByTotalPointsAndName(a, b);
    });

    const sortedData = this.dataTableSource;
    this.dataTableSource = [];
    this.dataTableSource.push(...sortedData);
    this.isLoading = false;
  }

  public compareComapanies(selectedCompanies: Company[]): void {
    this.router.navigate(['relatorios/comparar-empresas'], {
      queryParams: {
        userId: this.authenticationService.user.id,
        companies: JSON.stringify(mapIds(selectedCompanies))
      }
    });
  }

  public reload(): void {
    this.filter.filtersForm.updateValueAndValidity();
  }

  public autoRefresh(event: { checked: boolean }): void {
    if (event.checked) {
      this.intervalID = setInterval(() => this.reload(), this.autoReloadTime);
      return;
    }

    clearInterval(this.intervalID);
  }

  public roundResult(result): string {
    return (result % 1 === 0) ? result.toFixed(0) : result.toFixed(1);
  }

  public async createDefaultCriteriaGroup(): Promise<void> {
    this.isLoading = true;
    await this.companyRankingService.addCriteriasGroup(defaultCriteriaGroup);
    this.criteriasGroups = await this.companyRankingService.getCriteriasGroups();
    this.isLoading = false;
  }

  private orderByTotalPointsAndName(a, b): number {
    if (a.totalPoints === b.totalPoints) {
      return a.name.localeCompare(b.name);
    } else if (this.sortDirection === 'asc') {
      return a.totalPoints - b.totalPoints;
    } else {
      return b.totalPoints - a.totalPoints;
    }
  }

  public updateSelectedCriteriaGroup(): void {
    const selectedOptionHasBeenDeleted = !this.criteriasGroups.some((group) => group.id === this.filterStateManagerService.selectedOption.id);
    if (selectedOptionHasBeenDeleted) {
      this.filterStateManagerService.selectedOption = this.criteriasGroups[0];
    }
  }

  private setContentHeight(): void {
    if (!this.contentElementRef) {
      return;
    }

    const totalHeight = '100vh';
    const topHeaderHeight = '64px';
    const pageHeaderHeight = '63px';
    const totalGap = '66px';
    const filterHeight = this.filter.getHeight() + 'px';
    const selectedCompaniesHeight = '110px';
    let contentHeight = `calc(${totalHeight} - ${topHeaderHeight} - ${pageHeaderHeight} - ${totalGap} - ${filterHeight})`;

    if (this.selectedCompanies && this.selectedCompanies.length) {
      contentHeight = `calc(${contentHeight} - ${selectedCompaniesHeight})`;
    }

    this.contentElementRef.nativeElement.style.height = contentHeight;
  }

  private setTableColumnsWidth(): void {
    const trElement = document.querySelector('tr') as HTMLElement;
    const tableElement = document.querySelector('.company-ranking__content__table') as HTMLElement;
    if (!trElement || !tableElement) {
      return;
    }
    const trWidth = trElement.offsetWidth;
    const tableWidth = tableElement.offsetWidth;

    const newIsHorizontalScrollableTable = trWidth > tableWidth;

    if (newIsHorizontalScrollableTable !== this.isHorizontalScrollableTable) {
      this.isHorizontalScrollableTable = newIsHorizontalScrollableTable;
      this.cdr.detectChanges();
    }
  }

  @HostListener('click', ['$event'])
  handleClick(): void {
    this.setContentHeight();
  }
}
